package br.com.vvrefeicoes.portal.feature.users

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.file.PickedFile
import br.com.vvrefeicoes.portal.core.util.file.pickDocumentFile
import br.com.vvrefeicoes.portal.core.util.openExternalUrl
import br.com.vvrefeicoes.portal.core.util.prepareFileBrowserTab
import br.com.vvrefeicoes.portal.core.util.saveFile
import br.com.vvrefeicoes.portal.domain.model.UserDocument
import br.com.vvrefeicoes.portal.domain.model.UserDocumentType
import br.com.vvrefeicoes.portal.domain.repository.UserDocumentRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

class UserDocumentsController(
    private val userId: String,
    private val documentType: UserDocumentType,
    private val repository: UserDocumentRepository,
    private val scope: CoroutineScope,
) {
    var documents by mutableStateOf<List<UserDocument>>(emptyList())
        private set
    var pendingFiles by mutableStateOf<List<PickedFile>>(emptyList())
        private set
    var loading by mutableStateOf(true)
        private set
    var saving by mutableStateOf(false)
        private set
    var errorMessage by mutableStateOf<String?>(null)
        private set
    var successMessage by mutableStateOf<String?>(null)
        private set

    fun init() {
        scope.launch {
            loading = true
            errorMessage = null
            try {
                documents = repository.load(userId)
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar os documentos."
            } finally {
                loading = false
            }
        }
    }

    fun addFile(file: PickedFile) {
        pendingFiles = pendingFiles + file
    }

    fun pickNextFile() {
        scope.launch { pickDocumentFile()?.let(::addFile) }
    }

    fun removePending(file: PickedFile) {
        pendingFiles = pendingFiles - file
    }

    fun clearPending() {
        pendingFiles = emptyList()
    }

    fun save() {
        if (saving || pendingFiles.isEmpty()) return
        scope.launch {
            saving = true
            errorMessage = null
            successMessage = null
            try {
                pendingFiles.forEach { repository.upload(userId, it, documentType.source) }
                documents = repository.load(userId)
                pendingFiles = emptyList()
                successMessage = "Documentos enviados com sucesso."
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível enviar os documentos."
            } finally {
                saving = false
            }
        }
    }

    fun download(document: UserDocument) {
        scope.launch {
            runCatching {
                val bytes = repository.download(document.id)
                saveFile(document.originalFileName, document.contentType, bytes)
            }.onFailure { errorMessage = it.message ?: "Não foi possível baixar o documento." }
        }
    }

    fun open(document: UserDocument) {
        prepareFileBrowserTab()
        scope.launch {
            runCatching {
                openExternalUrl(repository.openUrl(document.id))
            }.onFailure { errorMessage = it.message ?: "Não foi possível abrir o documento." }
        }
    }

    fun delete(document: UserDocument) {
        scope.launch {
            runCatching {
                repository.delete(document.id)
                documents = documents.filterNot { it.id == document.id }
            }.onFailure { errorMessage = it.message ?: "Não foi possível excluir o documento." }
        }
    }
}

@Composable
fun rememberUserDocumentsController(
    userId: String,
    documentType: UserDocumentType,
): UserDocumentsController {
    val graph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember(userId, documentType) {
        UserDocumentsController(userId, documentType, graph.userDocumentRepository, scope)
    }
}
